import { createContext, type ReactNode, useContext, useEffect, useMemo, useState } from 'react'

export type Language = 'pt' | 'en'

type LanguageContextValue = {
  language: Language
  setLanguage: (language: Language) => void
  t: (text: string) => string
}

const languageStorageKey = 'nmap:language'

const translations: Record<string, string> = {
  'Agrupar por': 'Group by',
  De: 'From',
  Até: 'To',
  Aplicação: 'Application',
  'Aplicar filtros': 'Apply filters',
  'Período selecionado': 'Selected period',
  'Nenhuma aplicação disponível': 'No application available',
  'Nenhum ambiente disponível': 'No environment available',
  'Selecionar aplicações': 'Select applications',
  'Selecionar ambiente': 'Select environment',
  '0 - 20%': '0 - 20%',
  '21 - 40%': '21 - 40%',
  '41 - 60%': '41 - 60%',
  '61 - 80%': '61 - 80%',
  '81 - 100%': '81 - 100%',
  Ação: 'Action',
  'Abrir assistente': 'Open assistant',
  Alertas: 'Alerts',
  Ambiente: 'Environment',
  'Ambiente e Produto ainda não estão disponíveis no back.':
    'Environment and Product are not available from the backend yet.',
  'Acompanhamento de automações e verificações de saúde': 'Automation tracking and health checks',
  ANÁLISES: 'ANALYTICS',
  'Analisando dados...': 'Analyzing data...',
  'Ao vivo': 'Live',
  'Atualização Automática': 'Automatic Refresh',
  'Botão de usuário temporariamente oculto.': 'User button temporarily hidden.',
  'Canvas não disponível para captura.': 'Canvas is not available for capture.',
  Capturando: 'Capturing',
  'Cobertura horária': 'Hourly coverage',
  CONFIGURAÇÕES: 'SETTINGS',
  Configurações: 'Settings',
  'Detalhes da execução': 'Execution details',
  Data: 'Date',
  'dia completo': 'full day',
  'Distribuição de Status': 'Status Distribution',
  'Distribuição de status': 'Status distribution',
  Duração: 'Duration',
  'Escolha uma data': 'Choose a date',
  EXECUÇÃO: 'EXECUTION',
  Execução: 'Execution',
  Execuções: 'Executions',
  'Execuções com falha': 'Failed executions',
  Exportações: 'Exports',
  'Enviar pergunta': 'Send question',
  Falha: 'Failure',
  'falha(s)': 'failure(s)',
  Falhas: 'Failures',
  'Falhas no período': 'Failures in period',
  'Falhas por hora': 'Failures per hour',
  'Fechar assistente': 'Close assistant',
  'Fechar aviso do assistente': 'Close assistant notice',
  'Filtros temporariamente ocultos.': 'Filters temporarily hidden.',
  Funcionalidade: 'Functionality',
  'Funcionalidade ainda não disponível.': 'Feature not available yet.',
  Funcionalidades: 'Functionalities',
  'Health Map': 'Health Map',
  Hora: 'Hour',
  Horas: 'Hours',
  'horario comercial': 'business hours',
  Horário: 'Time',
  'Horário Comercial': 'Business Hours',
  'IA NMap': 'NMap AI',
  Limpar: 'Clear',
  'Limpar filtros': 'Clear filters',
  'Linha de Tendência de Falhas por Hora': 'Hourly Failure Trend Line',
  'Login Inválido': 'Invalid login',
  'Maior degradação': 'Highest degradation',
  Máx: 'Max',
  Máximo: 'Maximum',
  Média: 'Average',
  média: 'average',
  'mais lento que a média do dia': 'slower than the daily average',
  Mín: 'Min',
  Mínimo: 'Minimum',
  'Nao foi possivel consultar o assistente agora.': 'Unable to query the assistant right now.',
  'Nenhum dado disponível': 'No data available',
  nenhuma: 'none',
  'Nenhuma degradação detectada no filtro atual': 'No degradation detected in the current filter',
  'Nenhuma execução detalhada encontrada para este período.':
    'No detailed execution found for this period.',
  'Nenhuma execução lenta encontrada': 'No slow execution found',
  'Nenhuma falha encontrada': 'No failure found',
  'Nenhuma funcionalidade disponível': 'No functionality available',
  'Não foi possível gerar o PNG.': 'Unable to generate the PNG.',
  'Não foi possível renderizar a captura.': 'Unable to render the capture.',
  'Pergunta para o assistente': 'Question for the assistant',
  'Pergunte para IA Novigi': 'Ask Novigi AI',
  'Pergunte sobre falhas, lentidao, saude das funcionalidades ou horarios criticos.':
    'Ask about failures, slowness, functionality health, or critical times.',
  'Pergunte sobre os dados...': 'Ask about the data...',
  'Piores Intervalos': 'Worst Intervals',
  'Por Ambiente': 'By Environment',
  'Por CTS': 'By CTS',
  'Por Funcionalidade': 'By Functionality',
  'Por Produto': 'By Product',
  'Print da tela': 'Screen capture',
  Produto: 'Product',
  'Ranking de Lentidão': 'Slowness Ranking',
  RELATÓRIOS: 'REPORTS',
  Relatórios: 'Reports',
  'Resumo por Funcionalidade': 'Summary by Functionality',
  Saúde: 'Health',
  'Saúde Geral': 'Overall Health',
  'Selecionar funcionalidades': 'Select functionalities',
  selecionada: 'selected',
  selecionadas: 'selected',
  Senha: 'Password',
  'Sem dados': 'No data',
  'Sem dados de duração': 'No duration data',
  'Sem execuções detalhadas para o período': 'No detailed executions for the period',
  'Sem intervalos críticos': 'No critical intervals',
  'Sem links disponíveis': 'No links available',
  Submit: 'Submit',
  'Page not found': 'Page not found',
  Sucesso: 'Success',
  'sucesso(s)': 'success(es)',
  'Sucesso x Falha': 'Success vs Failure',
  SUCESSO: 'SUCCESS',
  Sucessos: 'Successes',
  Taxa: 'Rate',
  até: 'to',
  'Tempo médio': 'Average time',
  'Tempo Médio/Hora': 'Average Time/Hour',
  'Tempo médio de execução': 'Average execution time',
  'Tempo médio de resposta': 'Average response time',
  'Tempo médio de resposta por funcionalidade': 'Average response time by functionality',
  'Tempo total': 'Total time',
  'Tempo total no período': 'Total time in period',
  'Tema claro': 'Light theme',
  'Tema escuro': 'Dark theme',
  'Tendência de Tempo de Resposta': 'Response Time Trend',
  'Timeline por Funcionalidade': 'Timeline by Functionality',
  Todas: 'All',
  'Todos os ambientes': 'All environments',
  'Não informado': 'Not provided',
  'Todas Funcionalidades': 'All Functionalities',
  'Total de execuções': 'Total executions',
  'Total monitorado': 'Total monitored',
  'Top 5 Funcionalidades com Falhas': 'Top 5 Functionalities with Failures',
  'Última atualização': 'Last updated',
  'Últimas Execuções Lentas': 'Latest Slow Executions',
  'Últimas Falhas': 'Latest Failures',
  Usuários: 'Users',
  'Ver relatório': 'View report',
  'Visão Geral': 'Overview',
}

const LanguageContext = createContext<LanguageContextValue | undefined>(undefined)

function getInitialLanguage(): Language {
  if (typeof window === 'undefined') return 'pt'

  return window.localStorage.getItem(languageStorageKey) === 'en' ? 'en' : 'pt'
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<Language>(getInitialLanguage)

  useEffect(() => {
    if (typeof window === 'undefined') return

    window.localStorage.setItem(languageStorageKey, language)
    document.documentElement.lang = language === 'en' ? 'en' : 'pt-BR'
  }, [language])

  const value = useMemo<LanguageContextValue>(
    () => ({
      language,
      setLanguage: setLanguageState,
      t: text => (language === 'en' ? translations[text] || text : text),
    }),
    [language],
  )

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>
}

export function useLanguage() {
  const context = useContext(LanguageContext)
  if (!context) throw new Error('useLanguage must be used inside LanguageProvider')

  return context
}

export function LanguageToggle() {
  const { language, setLanguage } = useLanguage()

  return (
    <div
      aria-label="Language"
      className="flex h-9 items-center rounded-md border border-slate-200 bg-white p-1 text-xs font-black text-slate-600"
      role="group"
    >
      {(['pt', 'en'] as const).map(option => {
        const selected = language === option

        return (
          <button
            key={option}
            aria-pressed={selected}
            className={`h-7 rounded px-2.5 transition ${
              selected ? 'bg-emerald-100 text-emerald-800' : 'hover:bg-slate-50'
            }`}
            onClick={() => setLanguage(option)}
            type="button"
          >
            {option.toUpperCase()}
          </button>
        )
      })}
    </div>
  )
}
